import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Sparkles, Clapperboard, ArrowLeft, Bot, ShieldCheck, LogOut } from "lucide-react";
import { LanguageBar } from "./LanguageBar";
import { useSearch } from "../context/SearchContext";
import { useMyCinema } from "../context/MyCinemaContext";
import { useTranslate } from "../context/TranslateContext";
import { useAi } from "../context/AiContext";

// Single, thin, consistent top nav used on EVERY page. All items inline (no dropdowns);
// wraps to a second line if space runs out.
const TABS = [
  { label: "Movies", to: "/article/movies-overview" },
  { label: "Music", to: "/article/music-overview" },
  { label: "People", to: "/article/people-overview" },
  { label: "History", to: "/article/history-overview" },
  { label: "Community", to: "/article/community-overview" },
  { label: "Deep Dives", to: "/explore", testid: "nav-deepdives" },
  { label: "TV", to: "/article/television-overview" },
  { label: "Serials", to: "/article/serials-overview" },
  { label: "OTT", to: "/article/ott-overview" },
  { label: "Charts", to: "/charts" },
  { label: "Locations", to: "/locations" },
];

export const InnerNav = ({ back = true }) => {
  const { setOpen } = useSearch();
  const { user, setAuthOpen, isStaff, signOut } = useMyCinema();
  const { t } = useTranslate();
  const { openAi } = useAi();
  const navigate = useNavigate();

  return (
    <div className="sticky top-0 z-50" data-testid="navbar">
      <header className="border-b border-edge bg-paper/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] items-center gap-x-5 gap-y-1 px-5 py-2 md:px-10">
          {back && (
            <button onClick={() => navigate(-1)} data-testid="back-btn"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-edge text-cream transition-colors hover:border-gold hover:text-gold">
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <Link to="/" data-testid="inner-logo" className="flex shrink-0 items-baseline gap-1">
            <span className="font-display text-xl font-semibold text-cream">Indias</span>
            <span className="font-display text-xl font-semibold italic text-gradient-gold">Cinema</span>
          </Link>

          {/* All tabs inline — horizontal scroll on mobile, wrap on large screens */}
          <nav className="flex min-w-0 flex-1 items-center gap-x-4 overflow-x-auto whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:flex-wrap lg:gap-y-1 lg:overflow-visible lg:whitespace-normal">
            {TABS.map((l) => (
              <Link key={l.label} to={l.to} data-testid={l.testid || `nav-${l.label.toLowerCase()}`}
                className="group relative shrink-0 font-body text-sm text-ash transition-colors duration-300 hover:text-cream">
                {t(l.label)}
                <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </Link>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <button onClick={() => setOpen(true)} data-testid="inner-search"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-edge text-cream transition-colors hover:border-gold hover:text-gold">
              <Search className="h-4 w-4" />
            </button>
            <button onClick={() => openAi()} data-testid="nav-ask-ai"
              className="hidden items-center gap-1.5 rounded-full border border-gold/50 px-3 py-1.5 text-sm text-gold transition-colors hover:bg-gold/10 sm:inline-flex">
              <Bot className="h-4 w-4" /> {t("Ask AI")}
            </button>
            <Link to="/watch" data-testid="inner-watch"
              className="hidden items-center gap-1.5 rounded-full bg-gold px-3 py-1.5 text-sm font-medium text-ink transition-transform hover:scale-105 md:inline-flex">
              <Sparkles className="h-4 w-4" /> {t("What to Watch")}
            </Link>
            {user ? (
              <>
                {isStaff && (
                  <Link to="/admin/content" data-testid="inner-admin"
                    className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 px-3 py-1.5 text-sm text-gold hover:bg-gold/10">
                    <ShieldCheck className="h-4 w-4" /> <span className="hidden sm:inline">{t("Admin")}</span>
                  </Link>
                )}
                <Link to="/my-cinema" data-testid="inner-mycinema"
                  className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 px-3 py-1.5 text-sm text-gold">
                  <Clapperboard className="h-4 w-4" /> <span className="hidden sm:inline">{user.name.split(" ")[0]}</span>
                </Link>
                <button onClick={signOut} data-testid="inner-signout" title={t("Sign out")}
                  className="inline-flex items-center rounded-full border border-edge px-2.5 py-1.5 text-sm text-ash hover:border-gold hover:text-gold">
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : (
              <button onClick={() => setAuthOpen(true)} data-testid="inner-signin"
                className="inline-flex items-center gap-1.5 rounded-full border border-edge px-3 py-1.5 text-sm text-cream hover:border-gold hover:text-gold">
                <Clapperboard className="h-4 w-4" /> <span className="hidden sm:inline">{t("Log in")}</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Always-visible language second line (Indian languages) */}
      <LanguageBar />
    </div>
  );
};
